import { createStore, type StoreApi } from 'zustand/vanilla';

import type { ErrorKind, Message, PodcastGenStatus, RecorderState, StreamStatus } from './types';

export interface ChatStoreState {
  messages: Message[];
  streaming: { messageId: string | null; status: StreamStatus };
  recording: { state: RecorderState; durationMs: number; error: ErrorKind | null };
  podcastGen: Record<string, PodcastGenStatus>;
  ui: {
    open: boolean;
    errorOverlay: { visible: boolean; countdown: number };
  };
  inputText: string;
}

export interface ChatStoreActions {
  appendMessage: (m: Message) => void;
  patchMessage: (id: string, p: Partial<Message>) => void;
  removeMessage: (id: string) => void;

  setInputText: (s: string) => void;
  setOpen: (o: boolean) => void;

  setStreaming: (messageId: string | null, status: StreamStatus) => void;
  setRecording: (state: RecorderState, error?: ErrorKind | null) => void;
  setRecordingDuration: (ms: number) => void;
  setPodcastGen: (messageId: string, status: PodcastGenStatus) => void;

  showErrorOverlay: (seconds: number) => void;
  tickErrorOverlay: () => void;
  hideErrorOverlay: () => void;

  clearChat: (welcome?: Message) => void;
  resetTo: (state: ChatStoreState) => void;
}

export type ChatStore = ChatStoreState & ChatStoreActions;
export type ChatStoreApi = StoreApi<ChatStore>;

export const initialChatStoreState: ChatStoreState = {
  messages: [],
  streaming: { messageId: null, status: 'idle' },
  recording: { state: 'idle', durationMs: 0, error: null },
  podcastGen: {},
  ui: { open: true, errorOverlay: { visible: false, countdown: 0 } },
  inputText: '',
};

/**
 * Libera o object URL de mensagens de áudio do usuário (US 13335).
 *
 * Só mensagens `user-audio` têm URL criada localmente por nós; o áudio de
 * podcast vem do servidor e não deve ser revogado.
 */
function revokeOwnedObjectUrl(message: Message): void {
  if (message.kind !== 'user-audio') return;
  URL.revokeObjectURL(message.audioUrl);
}

export function createChatStore(initial: ChatStoreState = initialChatStoreState): ChatStoreApi {
  return createStore<ChatStore>()((set) => ({
    ...initial,

    appendMessage: (m) => set((s) => ({ messages: [...s.messages, m] })),

    patchMessage: (id, p) =>
      set((s) => ({
        messages: s.messages.map((m) => (m.id === id ? ({ ...m, ...p } as Message) : m)),
      })),

    removeMessage: (id) =>
      set((s) => {
        const doomed = s.messages.find((m) => m.id === id);
        if (doomed) revokeOwnedObjectUrl(doomed);
        return { messages: s.messages.filter((m) => m.id !== id) };
      }),

    setInputText: (s) => set({ inputText: s }),
    setOpen: (open) => set((s) => ({ ui: { ...s.ui, open } })),

    setStreaming: (messageId, status) => set({ streaming: { messageId, status } }),

    setRecording: (state, error = null) =>
      set((s) => ({ recording: { ...s.recording, state, error } })),

    setRecordingDuration: (durationMs) =>
      set((s) => ({ recording: { ...s.recording, durationMs } })),

    setPodcastGen: (messageId, status) =>
      set((s) => ({ podcastGen: { ...s.podcastGen, [messageId]: status } })),

    showErrorOverlay: (seconds) =>
      set((s) => ({
        ui: { ...s.ui, errorOverlay: { visible: true, countdown: seconds } },
      })),

    tickErrorOverlay: () =>
      set((s) => {
        const next = s.ui.errorOverlay.countdown - 1;
        if (next <= 0) {
          return { ui: { ...s.ui, errorOverlay: { visible: false, countdown: 0 } } };
        }
        return { ui: { ...s.ui, errorOverlay: { ...s.ui.errorOverlay, countdown: next } } };
      }),

    hideErrorOverlay: () =>
      set((s) => ({ ui: { ...s.ui, errorOverlay: { visible: false, countdown: 0 } } })),

    clearChat: (welcome) =>
      set((s) => {
        s.messages.forEach(revokeOwnedObjectUrl);
        return {
          messages: welcome ? [welcome] : [],
          streaming: { messageId: null, status: 'idle' },
          recording: { state: 'idle', durationMs: 0, error: null },
          podcastGen: {},
          inputText: '',
          ui: { ...s.ui, errorOverlay: { visible: false, countdown: 0 } },
        };
      }),

    resetTo: (next) => set({ ...next }),
  }));
}
